'use client';
import React from 'react';
import dynamic from 'next/dynamic';

const BarChart = dynamic(() => import('recharts').then((m) => m.BarChart), { ssr: false });
const Bar = dynamic(() => import('recharts').then((m) => m.Bar), { ssr: false });
const XAxis = dynamic(() => import('recharts').then((m) => m.XAxis), { ssr: false });
const YAxis = dynamic(() => import('recharts').then((m) => m.YAxis), { ssr: false });
const CartesianGrid = dynamic(() => import('recharts').then((m) => m.CartesianGrid), { ssr: false });
const Tooltip = dynamic(() => import('recharts').then((m) => m.Tooltip), { ssr: false });
const ResponsiveContainer = dynamic(() => import('recharts').then((m) => m.ResponsiveContainer), { ssr: false });
const Cell = dynamic(() => import('recharts').then((m) => m.Cell), { ssr: false });

interface CategoryData {
  category: string;
  correct: number;
  total: number;
  pct: number;
}

interface CategoryBreakdownChartProps {
  data: CategoryData[];
}

function CustomTooltipContent({ active, payload, label }: { active?: boolean; payload?: { value: number }[]; label?: string }) {
  if (!active || !payload || !payload.length) return null;
  const pct = payload[0]?.value ?? 0;
  return (
    <div
      className="rounded-xl px-4 py-3 text-sm"
      style={{
        background: 'rgba(17,24,39,0.95)',
        border: '1px solid rgba(45,63,92,0.8)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <div className="font-semibold text-foreground mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <div
          className="w-2 h-2 rounded-full"
          style={{ background: pct >= 86 ? '#10B981' : pct >= 60 ? '#F59E0B' : '#EF4444' }}
        />
        <span className="text-secondary-foreground">Accuracy:</span>
        <span className="font-mono-nums font-bold text-foreground">{pct}%</span>
      </div>
    </div>
  );
}

export default function CategoryBreakdownChart({ data }: CategoryBreakdownChartProps) {
  return (
    <div style={{ width: '100%', height: 260 }} aria-label="Category accuracy bar chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap="30%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(30,41,59,0.6)" vertical={false} />
          <XAxis
            dataKey="category"
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11, fontFamily: 'var(--font-sans)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: 'var(--muted-foreground)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <Tooltip content={<CustomTooltipContent />} cursor={{ fill: 'rgba(59,130,246,0.06)' }} />
          <Bar dataKey="pct" radius={[6, 6, 0, 0]}>
            {data.map((entry) => (
              <Cell
                key={`cell-${entry.category}`}
                fill={entry.pct >= 86 ? '#10B981' : entry.pct >= 60 ? '#F59E0B' : '#EF4444'}
                opacity={0.85}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}